import { z } from 'zod';

/**
 * Wareneingangsprüfung (ADR 0072): EIN Beleg je Lieferung zu einer Bestellung.
 * Zwei Stufen, symmetrisch zum Warenausgang — der Lagerist prüft am Tablet
 * (`GEPRUEFT`), das Sekretariat bucht den Bestand (`GEBUCHT`).
 */

export const goodsReceiptStatusSchema = z.enum(['OFFEN', 'GEPRUEFT', 'GEBUCHT', 'STORNIERT']);
export type GoodsReceiptStatus = z.infer<typeof goodsReceiptStatusSchema>;

export const goodsReceiptDeviationSchema = z.enum([
  'KEINE',
  'UNTERLIEFERUNG',
  'UEBERLIEFERUNG',
  'BESCHAEDIGT',
]);
export type GoodsReceiptDeviation = z.infer<typeof goodsReceiptDeviationSchema>;

/** Deutsche Labels für UI und PDF (CLAUDE.md §12). */
export const GOODS_RECEIPT_STATUS_LABELS: Record<GoodsReceiptStatus, string> = {
  OFFEN: 'Prüfung läuft',
  GEPRUEFT: 'Geprüft — wartet auf Buchung',
  GEBUCHT: 'Gebucht',
  STORNIERT: 'Storniert',
};

export const GOODS_RECEIPT_DEVIATION_LABELS: Record<GoodsReceiptDeviation, string> = {
  KEINE: 'Vollständig',
  UNTERLIEFERUNG: 'Zu wenig geliefert',
  UEBERLIEFERUNG: 'Zu viel geliefert',
  BESCHAEDIGT: 'Beschädigt — nicht einbuchen',
};

/**
 * Abweichungen, bei denen ein Foto Pflicht ist (Entscheidung Tech-Lead: nur bei
 * Abweichung). Bei Transportschaden ist es der Beweis gegenüber dem Lieferanten.
 */
export const DEVIATIONS_REQUIRING_PHOTO: readonly GoodsReceiptDeviation[] = [
  'UNTERLIEFERUNG',
  'UEBERLIEFERUNG',
  'BESCHAEDIGT',
];

/**
 * Abweichungen, bei denen eine Bemerkung Pflicht ist. Bei Überlieferung und
 * Schaden muss nachvollziehbar sein, was passiert ist — sonst steht später eine
 * unerklärte Mengenabweichung im Beleg.
 */
export const DEVIATIONS_REQUIRING_NOTE: readonly GoodsReceiptDeviation[] = [
  'UEBERLIEFERUNG',
  'BESCHAEDIGT',
];

/** Wird diese Menge in den Lagerbestand gebucht? Beschädigtes NICHT (ADR 0072). */
export function isBookable(deviation: GoodsReceiptDeviation): boolean {
  return deviation !== 'BESCHAEDIGT';
}

export const goodsReceiptPhotoResponseSchema = z.object({
  id: z.string().uuid(),
  goodsReceiptItemId: z.string().uuid().nullable(),
  fileName: z.string(),
  note: z.string().nullable(),
  createdAt: z.string().datetime(),
});
export type GoodsReceiptPhotoResponse = z.infer<typeof goodsReceiptPhotoResponseSchema>;

export const goodsReceiptItemResponseSchema = z.object({
  id: z.string().uuid(),
  purchaseOrderItemId: z.string().uuid(),
  position: z.number().int(),
  articleId: z.string().uuid().nullable(),
  articleNumber: z.string().nullable(),
  description: z.string(),
  /** Langtext aus der Bestellposition — an der Rampe oft die eigentliche Beschreibung. */
  longText: z.string().nullable(),
  unit: z.string().nullable(),
  /** Positions-Projekt der Bestellung (Snapshot) — für wen ist die Lieferung? */
  projectId: z.string().uuid().nullable(),
  projectCode: z.string().nullable(),
  projectName: z.string().nullable(),
  /** Snapshots aus der Bestellung zum Zeitpunkt der Prüfung. */
  orderedQty: z.number(),
  openQty: z.number(),
  checkedQty: z.number(),
  deviation: goodsReceiptDeviationSchema,
  note: z.string().nullable(),
  /** Reklamation zu dieser Position als QM-Prozess (ADR 0054) — null = keine. */
  qmProcessId: z.string().uuid().nullable(),
  qmProcessNumber: z.string().nullable(),
  photos: z.array(goodsReceiptPhotoResponseSchema),
});
export type GoodsReceiptItemResponse = z.infer<typeof goodsReceiptItemResponseSchema>;

/**
 * Kurzinfo eines eingehenden Transports (Anzeige am Beleg: „angeliefert mit").
 * Ohne Fahrzeugangaben — das Transportmodul führt keine LKWs mehr (ADR 0079).
 */
export const inboundTransportRefSchema = z.object({
  id: z.string().uuid(),
  number: z.string(),
  status: z.string(),
  /** Termin: geplante Ankunft. */
  plannedAt: z.string().datetime().nullable(),
  /** Tatsächlich angekommen. */
  completedAt: z.string().datetime().nullable(),
});
export type InboundTransportRef = z.infer<typeof inboundTransportRefSchema>;

export const goodsReceiptResponseSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  number: z.string(),
  status: goodsReceiptStatusSchema,
  purchaseOrderId: z.string().uuid(),
  /** Snapshot-Anzeige aus der Bestellung (für Liste und Kopfzeile). */
  purchaseOrderNumber: z.string(),
  supplierName: z.string(),
  /** Projekt des Bestellkopfs (Anzeige) — die Positionen können eigene Projekte tragen. */
  projectId: z.string().uuid().nullable(),
  projectCode: z.string().nullable(),
  supplierDeliveryNote: z.string().nullable(),
  note: z.string().nullable(),
  checkedBy: z.string().uuid().nullable(),
  checkedAt: z.string().datetime().nullable(),
  bookedBy: z.string().uuid().nullable(),
  bookedAt: z.string().datetime().nullable(),
  items: z.array(goodsReceiptItemResponseSchema),
  /** Fotos am Beleg selbst (ohne Positionsbezug) — z. B. die Palette an der Rampe. */
  photos: z.array(goodsReceiptPhotoResponseSchema),
  /**
   * Eingehende Transporte, die diese Bestellung angeliefert haben (ADR 0071/0072).
   * Abgeleitet aus der LKW-Zuordnung im Transport — nicht doppelt gespeichert.
   */
  inboundTransports: z.array(inboundTransportRefSchema),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type GoodsReceiptResponse = z.infer<typeof goodsReceiptResponseSchema>;

/** Listeneintrag — ohne Positionen, Fotos und Transporte, für die Übersicht. */
export const goodsReceiptListItemSchema = goodsReceiptResponseSchema
  .omit({ items: true, photos: true, inboundTransports: true })
  .extend({
    itemCount: z.number().int(),
    /** Wie viele Positionen eine Abweichung tragen (Ampel in der Liste). */
    deviationCount: z.number().int(),
  });
export type GoodsReceiptListItem = z.infer<typeof goodsReceiptListItemSchema>;

/** Erwartete Lieferung: offene Bestellung als Einstieg für den Lageristen. */
export const expectedDeliverySchema = z.object({
  purchaseOrderId: z.string().uuid(),
  number: z.string(),
  supplierName: z.string(),
  expectedDate: z.string().datetime().nullable(),
  projectCode: z.string().nullable(),
  projectName: z.string().nullable(),
  /** Positionen mit offener Restmenge. */
  openItemCount: z.number().int(),
  /** Läuft für diese Bestellung schon eine Prüfung? Dann dort weitermachen. */
  openGoodsReceiptId: z.string().uuid().nullable(),
});
export type ExpectedDelivery = z.infer<typeof expectedDeliverySchema>;

/**
 * Eine Bestellposition in der Lieferungs-Vorschau. Bewusst mit **allen drei**
 * Mengen: an der Rampe lautet die Frage nicht nur „was kommt noch?", sondern
 * genauso „was ist davon schon da?" — eine Teillieferung ist der Normalfall.
 */
export const expectedDeliveryItemSchema = z.object({
  id: z.string().uuid(),
  position: z.number().int(),
  /**
   * ARTICLE | TEXT | HEADING | PAGE_BREAK. Eine Bestellung ist gegliedert; die
   * Vorschau zeigt sie so, wie sie auf dem Papier steht. Nur `ARTICLE` trägt
   * Mengen — bei den übrigen wären „0 Stk" schlicht gelogen.
   */
  positionType: z.string(),
  articleNumber: z.string().nullable(),
  description: z.string(),
  longText: z.string().nullable(),
  unit: z.string().nullable(),
  orderedQty: z.number(),
  receivedQty: z.number(),
  /** Restmenge = bestellt − geliefert, nie negativ. */
  openQty: z.number(),
  /** Positions-Projekt der Bestellung (ADR 0040) — kann vom Kopf abweichen. */
  projectCode: z.string().nullable(),
  projectName: z.string().nullable(),
});
export type ExpectedDeliveryItem = z.infer<typeof expectedDeliveryItemSchema>;

/**
 * Vorschau einer erwarteten Lieferung: derselbe Kopf wie in der Liste, dazu die
 * Positionen. Eigene Abfrage statt eines dickeren Listen-Eintrags — die Liste
 * zeigt 50 Bestellungen, deren Positionen will man nicht alle mitschicken, nur
 * um eine davon anzusehen (Muster `DocumentPreviewButton`: erst beim Öffnen laden).
 */
export const expectedDeliveryDetailSchema = expectedDeliverySchema.extend({
  /** Bestellstatus — eine Prüfung ist nur bei DRAFT/ORDERED möglich. */
  status: z.string(),
  orderDate: z.string().datetime(),
  notes: z.string().nullable(),
  items: z.array(expectedDeliveryItemSchema),
});
export type ExpectedDeliveryDetail = z.infer<typeof expectedDeliveryDetailSchema>;

export const startGoodsReceiptSchema = z.object({
  purchaseOrderId: z.string().uuid(),
  /** Lieferschein-Nummer des Lieferanten (steht auf dem Papier an der Rampe). */
  supplierDeliveryNote: z.string().trim().max(120).optional(),
});
export type StartGoodsReceiptInput = z.infer<typeof startGoodsReceiptSchema>;

export const updateGoodsReceiptItemSchema = z.object({
  /** Gezählte Menge. Bei BESCHAEDIGT die angekommene, NICHT gebuchte Menge. */
  checkedQty: z.number().min(0).optional(),
  deviation: goodsReceiptDeviationSchema.optional(),
  note: z.string().trim().max(500).nullable().optional(),
});
export type UpdateGoodsReceiptItemInput = z.infer<typeof updateGoodsReceiptItemSchema>;

export const updateGoodsReceiptSchema = z.object({
  supplierDeliveryNote: z.string().trim().max(120).nullable().optional(),
  note: z.string().trim().max(1000).nullable().optional(),
});
export type UpdateGoodsReceiptInput = z.infer<typeof updateGoodsReceiptSchema>;

/**
 * Metadaten eines Fotos (die Bytes stehen im Request-Body, Muster wie Sperrlager).
 * `goodsReceiptItemId` ordnet das Foto einer Position zu — nur dann erfüllt es die
 * Foto-Pflicht bei Abweichung; ohne Zuordnung ist es ein allgemeines Beleg-Foto
 * (z. B. die Palette an der Rampe).
 */
export const uploadGoodsReceiptPhotoSchema = z.object({
  goodsReceiptItemId: z.string().uuid().nullish(),
  note: z.string().trim().max(500).nullish(),
});
export type UploadGoodsReceiptPhotoInput = z.infer<typeof uploadGoodsReceiptPhotoSchema>;

/**
 * Leitet die Abweichung aus gezählter und offener Menge ab — damit der Lagerist
 * sie nicht selbst wählen muss. `BESCHAEDIGT` ist NICHT ableitbar (das sieht nur
 * der Mensch) und wird deshalb nie automatisch gesetzt.
 */
export function deriveDeviation(checkedQty: number, openQty: number): GoodsReceiptDeviation {
  if (checkedQty > openQty) return 'UEBERLIEFERUNG';
  if (checkedQty < openQty) return 'UNTERLIEFERUNG';
  return 'KEINE';
}

/**
 * Reklamation aus einer abweichenden Position erzeugen (ADR 0072). Der Server baut
 * Titel und Problembeschreibung aus dem Beleg — hier nur, was der Mensch entscheidet:
 * Prozessart (4D reicht meist, 8D bei größeren Fällen) und eine Ergänzung.
 */
export const createComplaintSchema = z.object({
  type: z.enum(['FOUR_D', 'EIGHT_D']).default('FOUR_D'),
  /** Zusatz zur automatisch erzeugten Problembeschreibung (optional). */
  note: z.string().trim().max(2000).optional(),
});
export type CreateComplaintInput = z.infer<typeof createComplaintSchema>;
